import {type RumGlobal} from '@datadog/browser-rum';
import {useCallback, useEffect, useRef} from 'react';

import afterFrame from '../afterFrame';
import {useDatadogRum} from './datadogRum';
import {getDatadogRumViewTimings} from './datadogRumViewTimings';
import {
  getPerfMetricSession,
  markCustomerReadyFired,
} from './pageMetricSession';
import {
  type PerformanceMetricsContext,
  trackPageMetric,
} from './trackPageMetric';
import {useDurationVital} from './useDurationVital';

type Predicate = () => boolean;

type UseFirstTimingHook = () => void;

/**
 * Checks if a timing with the given name has already been triggered in the current view.
 *
 * @param name - The timing name to check
 * @returns True if the timing has been triggered, false otherwise
 */
export function hasTriggeredTiming(name: string) {
  return getDatadogRumViewTimings().has(name);
}

/**
 * The same as `datadogRum.addTiming`, but subsequent calls to the same timing
 * within the same Datadog view will not be recorded.
 *
 * By default, `datadogRum.addTiming` can be called multiple times, and the
 * *last* instance of it is used by Datadog in their metrics. This can be useful
 * sometimes, but usually what we want is to measure the *first* instance of
 * a timing concluding. This lets us record things like "when did the first
 * graph finish loading?" or "how long does it take to load a whole workspace?"
 *
 * @param datadogRum - The Datadog RUM instance
 * @param name - The timing name
 * @param time - Optional timestamp for the timing (defaults to now)
 */
export function addFirstTiming(
  datadogRum: RumGlobal,
  name: string,
  time?: number
): void {
  if (hasTriggeredTiming(name)) {
    return;
  }

  getDatadogRumViewTimings().add(name);
  datadogRum.addTiming(name, time);
}

/**
 * A version of Datadog's addTiming function that only fires once. It returns a
 * function that, when called, will trigger Datadog to track when the timing
 * occurred. It will record only the first time a particular metric is called
 * per-view. So if multiple components trigger the same timing, only the first
 * one will be noted by Datadog RUM.
 *
 * If you want to track when a component mounts on the page for the first time,
 * you can combine `useFirstTiming` and `useLayoutEffect` to achieve that:
 *
 * @example
 * ```js
 * useLayoutEffect(useFirstTiming('my_component_mounted'))
 * ```
 *
 * @param metric - The timing metric name
 * @param logAfterFrame - Whether to also log an after-frame timing (defaults to true)
 * @returns A callback function to trigger the timing
 */
export function useFirstTiming(
  metric: string,
  logAfterFrame = true
): UseFirstTimingHook {
  const datadog = useDatadogRum();

  return useCallback(() => {
    addFirstTiming(datadog, metric);
    if (logAfterFrame) {
      afterFrame(() => {
        addFirstTiming(datadog, `${metric}-afterFrame`);
      });
    }
  }, [datadog, metric, logAfterFrame]);
}

/**
 * Triggers Datadog's addTiming function for the provided metric the first time
 * the predicate function returns true.
 *
 * @example
 * ```js
 * const { data, loading } = useQuery(...)
 * useFirstTimingEventWhen('my_component_loaded', () => !loading)
 * ```
 *
 * @param metric - The timing metric name
 * @param predicate - A function that returns true when the timing should be recorded
 * @param logAfterFrame - Whether to also log an after-frame timing (defaults to true)
 */
export function useFirstTimingEventWhen(
  metric: string,
  predicate: Predicate,
  logAfterFrame = true
): void {
  const datadog = useDatadogRum();

  useEffect(() => {
    if (!hasTriggeredTiming(metric) && predicate()) {
      addFirstTiming(datadog, metric);
      if (logAfterFrame) {
        afterFrame(() => {
          addFirstTiming(datadog, `${metric}-afterFrame`);
        });
      }
    }
  });
}

const CUSTOMER_READY_VITAL = 'perf_customer_ready';

/**
 * Tracks when a page becomes ready for user interaction.
 *
 * - Records a Datadog RUM duration vital (`perf_customer_ready`) for custom metric dashboards.
 *   `pageName` and `pageGroup` are attached as vital context attributes for faceting.
 * - Sends a `customer_ready` action via trackPageMetric (for RUM Explorer + Segment fallback)
 *
 * @example
 * ```ts
 * const metricsContext = {pageName: PageViews.RegistriesOverview, organizationName: orgName};
 * useCustomerReadyWhen(metricsContext, () => allSectionsLoaded);
 * ```
 *
 * @param metricsContext - Must include `pageName` (fine-grained view identifier) plus any
 *   additional metadata to forward to analytics
 * @param predicate - Returns true when the page is ready
 */
export function useCustomerReadyWhen(
  metricsContext: PerformanceMetricsContext,
  _predicate: Predicate
): void {
  const session = getPerfMetricSession();

  // This shouldn't happen since the session is started at the Routing layer
  if (!session) {
    console.warn(
      'useCustomerReadyWhen: no perf metric session available. customer_ready timings and metrics will not be recorded. Ensure getPerfMetricSession() is initialized before calling useCustomerReadyWhen.'
    );
  }

  const pageGroup = session?.pageGroup ?? 'UnknownRoute';
  const {pageName} = metricsContext;
  // First customer_ready in a session uses session.startTime so that pre-component
  // loading (feature flags, project fetches, etc.) is included in the measurement.
  // After that, hasCustomerReadyFired is true and subsequent tab switches use
  // Date.now() so their duration is "time from tab click to content ready" rather
  // than "time from original route navigation".
  const mountTime = useRef(
    session?.hasCustomerReadyFired
      ? Date.now()
      : (session?.startTime ?? Date.now())
  );
  const {record} = useDurationVital(
    CUSTOMER_READY_VITAL,
    `${pageName} customer ready`
  );

  // Dedup keys are scoped to pageName so that navigating between tabs within the
  // same RUM view (e.g. RegistriesDiscoverRegistries → RegistriesDiscoverCollections)
  // each fires their own customer_ready. The vital name stays fixed for custom metrics.
  const timingDedup = `${CUSTOMER_READY_VITAL}-${pageName}`;

  const predicate = () => !!session && _predicate();
  useFirstTimingEventWhen(timingDedup, predicate);

  useEffect(() => {
    const analyticsTrack = `${timingDedup}-track`;
    if (!hasTriggeredTiming(analyticsTrack) && predicate()) {
      getDatadogRumViewTimings().add(analyticsTrack);
      const duration = Date.now() - mountTime.current;

      record(mountTime.current, duration, {pageName, pageGroup});
      markCustomerReadyFired();
      trackPageMetric(metricsContext, 'customer_ready', {
        duration: Math.round(duration),
      });
    }
  });
}
